import { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Grid,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './ListDocuments.css';
import Navbar from '../../components/Navbar/Navbar';
import API_URL from '../../config/api';
import { FaEye, FaTrash } from 'react-icons/fa';
import DownloadIcon from '../../assets/download.svg';
import { isUserAdmin } from '../../utils/auth';
import Footer from '../../components/Footer/Footer';
import useMediaQuery from '@mui/material/useMediaQuery';

const TIPO_RELATORIO_VALIDACAO = 'RELATÓRIOS DE VALIDAÇÃO';
const TIPO_NOTAS_TECNICAS = 'NOTAS TÉCNICAS';

const normalize = value =>
  (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getBeNumber = origem => {
  const match = String(origem || '')
    .trim()
    .match(/^BE\s*0*(\d+)$/i);

  return match ? Number.parseInt(match[1], 10) : null;
};

const compareOrigins = (a, b) => {
  const numeroA = getBeNumber(a);
  const numeroB = getBeNumber(b);

  if (numeroA !== null && numeroB !== null) return numeroA - numeroB;
  if (numeroA !== null) return -1;
  if (numeroB !== null) return 1;

  return String(a || '').localeCompare(String(b || ''), 'pt-BR');
};

const ListDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedOrigens, setSelectedOrigens] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const token = JSON.parse(atob(localStorage.getItem('token') || '""'));

  const handleOpenUpload = () => navigate('/upload');
  const isMobile = useMediaQuery('(max-width:600px)');

  const orderedTypes = [
    'Administrativos',
    'Financeiro',
    'Técnicos',
    TIPO_NOTAS_TECNICAS,
    'Indicadores',
    'Mapa Temático',
    'Modelo de Arquivos',
    TIPO_RELATORIO_VALIDACAO,
  ];

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await fetch(`${API_URL}/api/Documentos`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDocuments(data);
        } else if (response.status === 401) {
          toast.error('Sessão expirada, faça login novamente.');
          localStorage.clear();
          navigate('/');
        } else if (response.status === 403) {
          toast.warn('Acesso negado.');
        } else {
          throw new Error('Erro ao carregar documentos.');
        }
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [navigate, token]);

  const isPlanilha = fileName => {
    const extensoesPlanilha = ['.xlsx', '.xls', '.csv'];
    return extensoesPlanilha.some(ext =>
      fileName.toLowerCase().endsWith(ext)
    );
  };

  const handleView = async (url, fileName) => {
    // Abre a aba imediatamente para evitar bloqueio de popup enquanto o
    // documento é carregado com o token de autenticação.
    const previewWindow = window.open('', '_blank');

    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = fileName || 'Documento';
      previewWindow.document.body.innerHTML =
        '<p style="font-family:Arial,sans-serif;padding:20px">Carregando documento...</p>';
    }

    try {
      const response = await fetch(
        `${API_URL}/api/Documentos/abrir/${encodeURIComponent(url)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 403) {
        previewWindow?.close();
        toast.warn('Seu usuário não possui acesso aos documentos administrativos.');
        return;
      }

      if (response.status === 401) {
        previewWindow?.close();
        toast.error('Sessão expirada, faça login novamente.');
        localStorage.clear();
        navigate('/');
        return;
      }

      if (!response.ok) {
        throw new Error('Falha ao visualizar o documento.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      if (previewWindow) {
        previewWindow.location.href = objectUrl;
      } else {
        // Fallback caso o navegador tenha bloqueado a nova aba.
        const link = document.createElement('a');
        link.href = objectUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Dá tempo suficiente para a nova aba consumir o Blob.
      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60000);
    } catch (viewError) {
      previewWindow?.close();
      console.error('Erro ao visualizar documento:', viewError);
      toast.error('Erro ao visualizar o documento.');
    }
  };

  const handleDownload = async (url, fileName) => {
    try {
      const response = await fetch(
        `${API_URL}/api/Documentos/download/${encodeURIComponent(url)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Falha no download.');

      const blob = await response.blob();
      const link = document.createElement('a');
      const objectUrl = window.URL.createObjectURL(blob);

      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      toast.error('Erro ao baixar o arquivo.');
    }
  };

  const handleDelete = async (id, nome) => {
    const confirmDelete = window.confirm(
      `Deseja realmente excluir o documento "${nome}"?`
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_URL}/api/Documentos/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Documento deletado com sucesso.');
        setDocuments(prev => prev.filter(doc => doc.id !== id));
      } else {
        toast.error('Erro ao deletar o documento.');
      }
    } catch (deleteError) {
      toast.error('Erro de conexão ao tentar deletar.');
    }
  };

  const rawTypes = [
    ...new Set(documents.map(doc => (doc?.tipo ?? '').toString().trim())),
  ].filter(Boolean);

  const canonicalTypes = orderedTypes
    .map(type => {
      const existingType = rawTypes.find(
        rawType => normalize(rawType) === normalize(type)
      );

      if (existingType) {
        return normalize(type) === normalize(TIPO_NOTAS_TECNICAS)
          ? TIPO_NOTAS_TECNICAS
          : existingType;
      }

      const shouldAlwaysBeVisible = [
        TIPO_NOTAS_TECNICAS,
        TIPO_RELATORIO_VALIDACAO,
      ].some(alwaysVisibleType =>
        normalize(type) === normalize(alwaysVisibleType)
      );

      return shouldAlwaysBeVisible ? type : null;
    })
    .filter(Boolean);

  const extraTypes = rawTypes.filter(
    rawType =>
      !orderedTypes.some(type => normalize(rawType) === normalize(type))
  );

  const types = [...canonicalTypes, ...extraTypes];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectedOrigens([]);
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
  };

  const currentTipo = types[selectedTab];
  const isValidationReportsTab =
    normalize(currentTipo) === normalize(TIPO_RELATORIO_VALIDACAO);

  const documentsFilteredByType = documents.filter(
    doc => normalize(doc.tipo) === normalize(currentTipo)
  );

  const originsFiltered = [
    ...new Set(
      documentsFilteredByType
        .map(doc => doc.origem)
        .filter(origem => origem && origem.trim())
    ),
  ].sort(compareOrigins);

  const filteredDocuments = documentsFilteredByType
    .filter(
      doc =>
        selectedOrigens.length === 0 ||
        selectedOrigens.includes(doc.origem)
    )
    .filter(doc =>
      doc.nome.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(doc => {
      const date = new Date(doc.dataUpload);
      if (startDate && date < startDate.startOf('day').toDate()) return false;
      if (endDate && date > endDate.endOf('day').toDate()) return false;
      return true;
    });

  const groupedValidationDocuments = Object.entries(
    filteredDocuments.reduce((groups, doc) => {
      const origem = doc.origem?.trim() || 'SEM BE';
      groups[origem] = [...(groups[origem] || []), doc];
      return groups;
    }, {})
  ).sort(([origemA], [origemB]) => compareOrigins(origemA, origemB));

  const renderDocumentsTable = (
    documentsToRender,
    { showType = true, showOrigin = true } = {}
  ) => (
    <div className="documents-table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>Nome</th>
            {!isMobile && showType && <th>Tipo</th>}
            {!isMobile && showOrigin && <th>Origem</th>}
            <th>Data Upload</th>
            <th className="th-center">Visualizar</th>
            <th className="th-center">Download</th>
            {isUserAdmin() && <th className="th-center">Excluir</th>}
          </tr>
        </thead>
        <tbody>
          {documentsToRender.map(doc => (
            <tr key={doc.id}>
              <td>{doc.nome}</td>
              {!isMobile && showType && <td>{doc.tipo}</td>}
              {!isMobile && showOrigin && <td>{doc.origem}</td>}
              <td>{new Date(doc.dataUpload).toLocaleString('pt-BR')}</td>
              <td>
                {isPlanilha(doc.nome) ? (
                  <button
                    className="button-icon"
                    disabled
                    title="Visualização indisponível para planilhas"
                  >
                    <FaEye size={20} className="icon-visualizar" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button-icon"
                    title="Visualizar Documento"
                    onClick={() => handleView(doc.url, doc.nome)}
                  >
                    <FaEye size={20} className="icon-visualizar" />
                  </button>
                )}
              </td>
              <td>
                <button
                  className="button-icon"
                  onClick={() => handleDownload(doc.url, doc.nome)}
                  title="Download"
                >
                  <img
                    src={DownloadIcon}
                    alt="Download"
                    className="icon-download"
                  />
                </button>
              </td>
              {isUserAdmin() && (
                <td>
                  <button
                    className="button-icon"
                    onClick={() => handleDelete(doc.id, doc.nome)}
                    title="Excluir Documento"
                  >
                    <FaTrash size={18} style={{ color: '#dc2626' }} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <p>Carregando documentos...</p>;
  if (error) return <p>Erro: {error}</p>;

  return (
    <div className="layout-wrapper">
      <Navbar />
      <div className="container">
        <h2 className="title">📄 Documentos</h2>

        {isUserAdmin() && (
          <button className="save-button" onClick={handleOpenUpload}>
            ➕ Salvar Novo Documento
          </button>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {types.map(type => (
              <Tab key={normalize(type)} label={type} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mb: 3 }} className="filters">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por nome"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Data início"
                  value={startDate}
                  onChange={newValue => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Data fim"
                  value={endDate}
                  onChange={newValue => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormGroup row sx={{ flexWrap: 'wrap' }}>
            {originsFiltered.map(origem =>
              isMobile ? null : (
                <FormControlLabel
                  key={origem}
                  control={
                    <Checkbox
                      checked={selectedOrigens.includes(origem)}
                      onChange={event => {
                        const checked = event.target.checked;
                        setSelectedOrigens(prev =>
                          checked
                            ? [...prev, origem]
                            : prev.filter(item => item !== origem)
                        );
                      }}
                      name={origem}
                    />
                  }
                  label={origem}
                  sx={{ mr: 2, mb: 1 }}
                />
              )
            )}
          </FormGroup>
        </Box>

        {filteredDocuments.length === 0 ? (
          <div className="empty-documents">
            Nenhum documento encontrado nesta categoria.
          </div>
        ) : isValidationReportsTab ? (
          <div className="validation-report-groups">
            {groupedValidationDocuments.map(([origem, groupDocuments]) => (
              <section key={origem} className="validation-report-group">
                <div className="validation-report-header">
                  <h3>{origem}</h3>
                  <span>
                    {groupDocuments.length}{' '}
                    {groupDocuments.length === 1 ? 'documento' : 'documentos'}
                  </span>
                </div>
                {renderDocumentsTable(groupDocuments, {
                  showType: false,
                  showOrigin: false,
                })}
              </section>
            ))}
          </div>
        ) : (
          renderDocumentsTable(filteredDocuments)
        )}

        <ToastContainer />
      </div>
      <Footer />
    </div>
  );
};

export default ListDocuments;
